import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { contributionsAPI, hubError } from '../lib/api';
import { useLiveEvents } from '../context/LiveEventsContext';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { DollarSign, Loader2, Plus, User, Receipt, MoreVertical, Pencil, History } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';

const statusMap = {
  paid: { label: 'Pagado', color: 'bg-green-100 text-green-700' },
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  failed: { label: 'Fallido', color: 'bg-red-100 text-red-700' },
  canceled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};

const methodLabels = { cash: 'Efectivo', transfer: 'Transferencia', manual: 'Manual', other: 'Otro', caurapay: 'CauraPay' };
const bs = (n) => `Bs. ${Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CURRENCIES = [
  { code: 'VES', label: 'Bolívares (Bs.)' },
  { code: 'USD', label: 'Dólares (USD)' },
  { code: 'EUR', label: 'Euros (EUR)' },
  { code: 'USDT', label: 'Tether (USDT)' },
];
const emptyForm = { currency: 'VES', amount: '', exchange_rate: '', category_name: 'Diezmo', donor_name: '', note: '' };
const LIVE_CONTRIBUTION_EVENTS = ['contribution.paid', 'contribution.updated'];

const EDITABLE_STATUSES = ['paid', 'pending'];
const isEditable = (item) => item.payment_method === 'cash' && EDITABLE_STATUSES.includes(item.status);
const toInput = (n) => (n === null || n === undefined ? '' : String(n));
const dateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

export default function ContributionsPage() {
  const { selectedChurchId, canCreate, canUpdate } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [history, setHistory] = useState(null);
  const [total, setTotal] = useState(0);
  const pg = usePagination(String(selectedChurchId));

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([contributionsAPI.list(pg.params), contributionsAPI.summary(selectedChurchId)])
      .then(([listRes, sumRes]) => {
        setItems(listRes.data?.data || []);
        setTotal(listRes.data?.total || 0);
        setSummary(sumRes.data?.data || null);
      })
      .catch(err => toast.error(hubError(err, 'Error al cargar aportes')))
      .finally(() => setLoading(false));
  }, [selectedChurchId, pg.params]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useLiveEvents(LIVE_CONTRIBUTION_EVENTS, (events) => {
    const last = events[events.length - 1];
    if (last.event === 'contribution.updated') {
      toast.info(`Aporte corregido: ${bs(last.payload?.previous_amount_ves)} → ${bs(last.payload?.amount_ves)}`);
    } else {
      toast.success(`Nuevo aporte recibido: ${bs(last.payload?.amount_ves)}`);
    }
    fetchData();
  });

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      amount_ves: toInput(item.amount_ves),
      amount_usd: toInput(item.amount_usd),
      bcv_rate: toInput(item.bcv_rate),
      donor_name: item.donor_name || '',
      note: item.note || '',
      paid_at: dateInput(item.paid_at || item.created_at),
      edit_reason: '',
    });
  };

  const handleEditSave = async () => {
    if (!editForm.edit_reason || editForm.edit_reason.trim().length < 3) {
      toast.error('Indica el motivo de la corrección (mínimo 3 caracteres)');
      return;
    }
    const payload = { edit_reason: editForm.edit_reason.trim() };
    const numeric = { amount_ves: 'amount_ves', amount_usd: 'amount_usd', bcv_rate: 'bcv_rate' };
    Object.keys(numeric).forEach(key => {
      if (editForm[key] !== toInput(editItem[key]) && editForm[key] !== '') {
        payload[key] = parseFloat(editForm[key]);
      }
    });
    if (editForm.donor_name !== (editItem.donor_name || '')) payload.donor_name = editForm.donor_name;
    if (editForm.note !== (editItem.note || '')) payload.note = editForm.note;
    if (editForm.paid_at && editForm.paid_at !== dateInput(editItem.paid_at || editItem.created_at)) {
      payload.paid_at = new Date(`${editForm.paid_at}T12:00:00`).toISOString();
    }
    if (Object.keys(payload).length === 1) { toast.error('No hay cambios que guardar'); return; }
    if (payload.amount_ves !== undefined && !(payload.amount_ves > 0)) { toast.error('El monto en Bs. debe ser mayor a 0'); return; }

    setEditSaving(true);
    try {
      await contributionsAPI.update(editItem.id, payload);
      toast.success('Aporte corregido');
      setEditItem(null);
      setEditForm(null);
      fetchData();
    } catch (err) { toast.error(hubError(err, 'Error al corregir el aporte')); }
    finally { setEditSaving(false); }
  };

  const openHistory = async (item) => {
    setHistoryItem(item);
    setHistory(null);
    try {
      const res = await contributionsAPI.history(item.id);
      setHistory(res.data?.data || res.data || {});
    } catch (err) {
      toast.error(hubError(err, 'No se pudo cargar el historial'));
      setHistory({ history: [] });
    }
  };

  const rowActions = (item, testSuffix = '') => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`contribution-actions${testSuffix}-${item.id}`}>
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canUpdate('contributions') && isEditable(item) && (
          <DropdownMenuItem onClick={() => openEdit(item)} data-testid={`contribution-edit${testSuffix}-${item.id}`}>
            <Pencil className="w-4 h-4 mr-2 text-[#056A84]" /> Corregir aporte
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => openHistory(item)} data-testid={`contribution-history${testSuffix}-${item.id}`}>
          <History className="w-4 h-4 mr-2 text-muted-foreground" /> Ver historial
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const editEquivalent = editForm
    ? (parseFloat(editForm.amount_usd) || 0) * (parseFloat(editForm.bcv_rate) || 0)
    : 0;

  const handleSave = async () => {
    const amount = parseFloat(form.amount);
    const rate = parseFloat(form.exchange_rate);
    if (!amount || amount <= 0) { toast.error('Ingresa un monto válido'); return; }
    if (form.currency !== 'VES' && (!rate || rate <= 0)) {
      toast.error('Indica la tasa de cambio en Bs.');
      return;
    }
    setSaving(true);
    try {
      await contributionsAPI.createManual({
        currency: form.currency,
        amount,
        exchange_rate: form.currency === 'VES' ? undefined : rate,
        category_name: form.category_name || null,
        donor_name: form.donor_name || null,
        note: form.note || null,
      });
      toast.success('Aporte en efectivo registrado');
      setDialogOpen(false);
      setForm(emptyForm);
      fetchData();
    } catch (err) { toast.error(hubError(err, 'Error al registrar el aporte')); } finally { setSaving(false); }
  };

  const equivalentVes = form.currency === 'VES'
    ? parseFloat(form.amount) || 0
    : (parseFloat(form.amount) || 0) * (parseFloat(form.exchange_rate) || 0);

  return (
    <div>
      <TopBar title="Aportes" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo}
        actionButton={canCreate('contributions') && (
          <Button className="bg-[#056A84] hover:bg-[#045A70] text-white btn-lift" onClick={() => setDialogOpen(true)} data-testid="contributions-create-button">
            <Plus className="w-4 h-4 mr-1" /> Registrar
          </Button>
        )}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="stat-card">
            <CardContent className="p-4">
              <p className="text-xl font-semibold text-[#056A84]" data-testid="contributions-total-ves">{bs(summary?.total_ves)}</p>
              <p className="text-xs text-muted-foreground">Total recaudado</p>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <p className="text-xl font-semibold text-[#1F9D7A]" data-testid="contributions-total-usd">${Number(summary?.total_usd || 0).toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">Equivalente USD</p>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <p className="text-xl font-semibold text-foreground" data-testid="contributions-record-count">{total}</p>
              <p className="text-xs text-muted-foreground">Registros</p>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <p className="text-xl font-semibold text-[#C58B2A]">{summary?.by_category?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Categorías</p>
            </CardContent>
          </Card>
        </div>

        {summary?.by_category?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="contributions-categories">
            {summary.by_category.map(cat => (
              <div key={cat.name} className="px-3 py-2 rounded-lg border bg-card">
                <p className="text-xs text-muted-foreground">{cat.name} · {cat.count}</p>
                <p className="text-sm font-semibold text-[#056A84]">{bs(cat.amount_ves)}</p>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><DollarSign className="w-12 h-12 text-muted-foreground/40" /><p>No hay aportes registrados</p></div>
        ) : (
          <>
            <Pagination
              total={total} page={pg.page} pageSize={pg.pageSize}
              onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
              label="aportes" testId="contributions-pagination"
            />
            <div className="hidden md:block rounded-xl border bg-card overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b bg-muted/30">
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Donante</th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Monto</th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Categoría</th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Método</th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Estado</th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Fecha</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase">Acciones</th>
                </tr></thead>
                <tbody>
                  {items.map(item => {
                    const si = statusMap[item.status] || statusMap.pending;
                    return (
                      <tr key={item.id} className="border-b table-row-hover" data-testid={`contribution-row-${item.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#00CCFF]/10 flex items-center justify-center text-xs font-medium text-[#056A84]">
                              {(item.donor_name || 'A')[0]}
                            </div>
                            <span className="text-sm">{item.donor_name || 'Anónimo'}</span>
                          </div>
                        </td>
                        <td className="p-3 text-sm font-semibold">
                          {bs(item.amount_ves)}
                          <span className="block text-[11px] font-normal text-muted-foreground">${Number(item.amount_usd || 0).toFixed(2)}</span>
                        </td>
                        <td className="p-3 text-sm">{item.category_name || '-'}</td>
                        <td className="p-3 text-sm">{methodLabels[item.payment_method] || item.payment_method}</td>
                        <td className="p-3"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${si.color}`}>{si.label}</span></td>
                        <td className="p-3 text-sm">{item.paid_at || item.created_at ? new Date(item.paid_at || item.created_at).toLocaleDateString('es-ES') : '-'}</td>
                        <td className="p-3 text-right">{rowActions(item)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {items.map(item => {
                const si = statusMap[item.status] || statusMap.pending;
                return (
                  <Card key={item.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">{bs(item.amount_ves)}</span>
                        <div className="flex items-center gap-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${si.color}`}>{si.label}</span>
                          {rowActions(item, '-mobile')}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <User className="w-3 h-3" /> {item.donor_name || 'Anónimo'}
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Receipt className="w-3 h-3" /> {item.category_name || '-'} · {methodLabels[item.payment_method] || item.payment_method}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Registrar aporte en efectivo</DialogTitle>
            <DialogDescription>
              Solo aportes recibidos en efectivo, en bolívares o en divisas. Las transferencias,
              pago móvil y débito inmediato se registran automáticamente desde la app.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Moneda *</Label>
                <Select value={form.currency} onValueChange={v => setForm({...form, currency: v, exchange_rate: v === 'VES' ? '' : form.exchange_rate})}>
                  <SelectTrigger data-testid="contribution-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Monto recibido *</Label>
                <Input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="contribution-amount-input" />
              </div>
            </div>
            {form.currency !== 'VES' && (
              <div className="space-y-2">
                <Label>Tasa de cambio (Bs. por {form.currency}) *</Label>
                <Input type="number" min="0" step="0.01" value={form.exchange_rate} onChange={e => setForm({...form, exchange_rate: e.target.value})} data-testid="contribution-rate-input" />
                <p className="text-xs text-muted-foreground" data-testid="contribution-equivalent">
                  Equivale a {bs(equivalentVes)}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Select value={form.category_name} onValueChange={v => setForm({...form, category_name: v})}>
                <SelectTrigger data-testid="contribution-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Diezmo">Diezmo</SelectItem>
                  <SelectItem value="Ofrenda">Ofrenda</SelectItem>
                  <SelectItem value="Misiones">Misiones</SelectItem>
                  <SelectItem value="Otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Donante</Label><Input value={form.donor_name} onChange={e => setForm({...form, donor_name: e.target.value})} placeholder="Opcional" data-testid="contribution-donor-input" /></div>
            <div className="space-y-2"><Label>Nota</Label><Input value={form.note} onChange={e => setForm({...form, note: e.target.value})} placeholder="Opcional" data-testid="contribution-note-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={handleSave} disabled={saving} data-testid="contribution-save-button">
              {saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : null}Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editItem} onOpenChange={(open) => { if (!open) { setEditItem(null); setEditForm(null); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="contribution-edit-dialog">
          <DialogHeader>
            <DialogTitle>Corregir aporte</DialogTitle>
            <DialogDescription>
              Solo se corrigen aportes en efectivo. Si cambias el monto en divisa y la tasa,
              el total en bolívares se recalcula automáticamente.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monto en Bs.</Label>
                  <Input type="number" min="0" step="0.01" value={editForm.amount_ves}
                    onChange={e => setEditForm({ ...editForm, amount_ves: e.target.value })}
                    data-testid="contribution-edit-ves" />
                </div>
                <div className="space-y-2">
                  <Label>Monto en divisa (USD)</Label>
                  <Input type="number" min="0" step="0.01" value={editForm.amount_usd}
                    onChange={e => setEditForm({ ...editForm, amount_usd: e.target.value })}
                    data-testid="contribution-edit-usd" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tasa BCV (Bs. por USD)</Label>
                <Input type="number" min="0" step="0.01" value={editForm.bcv_rate}
                  onChange={e => setEditForm({ ...editForm, bcv_rate: e.target.value })}
                  data-testid="contribution-edit-rate" />
                {editEquivalent > 0 && (
                  <p className="text-xs text-muted-foreground" data-testid="contribution-edit-equivalent">
                    Divisa × tasa = {bs(editEquivalent)}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Donante</Label>
                  <Input value={editForm.donor_name}
                    onChange={e => setEditForm({ ...editForm, donor_name: e.target.value })}
                    data-testid="contribution-edit-donor" />
                </div>
                <div className="space-y-2">
                  <Label>Fecha del aporte</Label>
                  <Input type="date" value={editForm.paid_at}
                    onChange={e => setEditForm({ ...editForm, paid_at: e.target.value })}
                    data-testid="contribution-edit-date" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Nota</Label>
                <Input value={editForm.note}
                  onChange={e => setEditForm({ ...editForm, note: e.target.value })}
                  data-testid="contribution-edit-note" />
              </div>
              <div className="space-y-2">
                <Label>Motivo de la corrección *</Label>
                <Input value={editForm.edit_reason} placeholder="Ej: monto digitado con un cero de más"
                  onChange={e => setEditForm({ ...editForm, edit_reason: e.target.value })}
                  data-testid="contribution-edit-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditItem(null); setEditForm(null); }} data-testid="contribution-edit-cancel">Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={handleEditSave} disabled={editSaving} data-testid="contribution-edit-save">
              {editSaving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : null}Guardar corrección
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyItem} onOpenChange={(open) => { if (!open) { setHistoryItem(null); setHistory(null); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="contribution-history-dialog">
          <DialogHeader>
            <DialogTitle>Historial de correcciones</DialogTitle>
            <DialogDescription>
              {historyItem ? `${historyItem.donor_name || 'Anónimo'} · ${bs(historyItem.amount_ves)}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {!history ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 spinner text-[#056A84]" /></div>
            ) : (history.history || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="contribution-history-empty">
                Este aporte no ha sido corregido.
              </p>
            ) : (
              <div className="space-y-3">
                {(history.history || []).map((h, i) => (
                  <div key={i} className="rounded-lg border p-3" data-testid={`contribution-history-entry-${i}`}>
                    <p className="text-sm font-medium text-foreground">{h.edit_reason || 'Corrección'}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {h.edited_by_email || h.edited_by_name || h.edited_by || 'Ministro'}
                      {(h.created_at || h.edited_at) ? ` · ${new Date(h.created_at || h.edited_at).toLocaleString('es-ES')}` : ''}
                    </p>
                    {h.before && h.after && (
                      <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
                        {Object.keys(h.after)
                          .filter(k => String(h.before[k] ?? '') !== String(h.after[k] ?? ''))
                          .map(k => (
                            <span key={k} className="block">
                              {k}: <span className="line-through">{String(h.before[k] ?? '-')}</span> → <span className="text-foreground">{String(h.after[k] ?? '-')}</span>
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
